import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useUnitInbox, useMarkUnitRead } from "../../../features/unitNotifications/queries";
import { TONE_ACCENT, TONE_CLASS, ago, typeMeta, type UnitNotification, notificationTitle } from "../../../features/unitNotifications/unitNotifications";
import NotificationDetail from "../../../features/unitNotifications/NotificationDetail";
import { revealUnit } from "../../../features/unitNotifications/revealUnit";
import { useLiteNotifications } from "../../../features/notifications/liteQueries";
import { NotificationsSection } from "../AppsSidebar/components";

interface Props {
  isDarkMode: boolean;
  buttonClass: string;
}

/**
 * Campana del header, en todas las apps (Daniel, 26-oct: «Alertas y
 * Notificaciones» sale de la appsuite y vive aquí). Dos pestañas:
 * - Unidades: notificaciones de unidad (eventos GPS, geocercas, viajes), las
 *   últimas 50 con leído por usuario; al tocar una se despliega con sus
 *   detalles y «Ver en el mapa» selecciona la unidad.
 * - Plataforma: los avisos y reportes en proceso que antes estaban en la
 *   appsuite (la misma NotificationsSection, sin su encabezado).
 */
export default function UnitNotificationsBell({ isDarkMode, buttonClass }: Props) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"units" | "platform">("units");
  const { unreadCount: platformUnread } = useLiteNotifications();
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // El panel va en un portal a <body>: dentro del header, un ancestro cambia la
  // referencia de `fixed` y en 390 px se salía de la pantalla.
  const [pos, setPos] = useState<{ top: number; right: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const right = Math.max(16, window.innerWidth - r.right);
      // 24rem en escritorio; en móvil, lo que quede con 16 px de margen a la izquierda.
      setPos({ top: r.bottom + 8, right, width: Math.min(384, window.innerWidth - right - 16) });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);
  const { data, isLoading, isError } = useUnitInbox(true);
  const markRead = useMarkUnitRead();
  const [expanded, setExpanded] = useState<string | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const unitUnread = data?.unread ?? 0;
  const unread = unitUnread + platformUnread;

  // Cada vez que se abre, la lista empieza cerrada: una desplegada de la vez anterior confunde.
  useEffect(() => {
    if (!open) setExpanded(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Como en la campanita de cada unidad: tocarla la despliega con sus
  // detalles y la marca leída; «Ver en el mapa» selecciona la unidad como si
  // se tocara en la lista (y, desde otra app, lleva primero al mapa).
  const onItem = (n: UnitNotification) => {
    if (!n.read) markRead.mutate({ ids: [n.id] });
    setExpanded((id) => (id === n.id ? null : n.id));
  };
  const onMap = (n: UnitNotification) => {
    if (!n.device_id) return;
    revealUnit(n.device_id);
    if (!pathname.startsWith("/map")) navigate("/map");
    setOpen(false);
  };

  const panel = isDarkMode ? "bg-dark-card border-dark-border text-white" : "bg-white border-gray-200 text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const hover = isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `Notificaciones: ${unread} sin leer` : "Notificaciones"}
        aria-expanded={open}
        className={`relative ${buttonClass}`}
      >
        <i className="ri-notification-3-line text-[17px]" aria-hidden="true"></i>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-[18px] text-center tabular-nums">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notificaciones"
          style={{ top: pos.top, right: pos.right, width: pos.width }}
          className={`fixed border rounded-xl shadow-xl z-[60] overflow-hidden ${panel}`}
        >
          <div className={`flex items-center justify-between gap-2 px-3 pt-3 pb-2 border-b ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
            <div role="tablist" aria-label="Tipo de notificación" className={`flex p-0.5 rounded-lg ${isDarkMode ? "bg-white/5" : "bg-gray-100"}`}>
              {([
                ["units", "Unidades", unitUnread],
                ["platform", "Plataforma", platformUnread],
              ] as const).map(([id, label, n]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={tab === id}
                  onClick={() => setTab(id)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${tab === id ? (isDarkMode ? "bg-dark-card text-white shadow" : "bg-white text-gray-900 shadow") : muted}`}
                >
                  {label}
                  {n > 0 && <span className="ml-1.5 tabular-nums text-red-500">{n > 9 ? "9+" : n}</span>}
                </button>
              ))}
            </div>
            {tab === "units" && unitUnread > 0 && (
              <button type="button" onClick={() => markRead.mutate({ all: true })} className="text-xs text-primary-500 hover:underline">
                Marcar todas como leídas
              </button>
            )}
          </div>
          {tab === "platform" && (
            <div className="max-h-[60vh] overflow-y-auto p-3">
              <NotificationsSection isDarkMode={isDarkMode} bare />
            </div>
          )}
          {tab === "units" && (
          <ul className={`max-h-[60vh] overflow-y-auto divide-y ${isDarkMode ? "divide-dark-border" : "divide-gray-100"}`}>
            {isLoading && <li className={`px-4 py-6 text-sm text-center ${muted}`}>Cargando…</li>}
            {isError && <li className={`px-4 py-6 text-sm text-center ${muted}`}>No se pudieron cargar las notificaciones.</li>}
            {data && data.items.length === 0 && (
              <li className={`px-4 py-8 text-sm text-center ${muted}`}>
                <i className="ri-notification-off-line text-2xl block mb-1" aria-hidden="true"></i>
                Sin notificaciones de unidades. Actívalas en Configuración → Alertas.
              </li>
            )}
            {data?.items.map((n) => {
              const meta = typeMeta(n.type);
              return (
                <li key={n.id} className={`relative ${expanded === n.id ? `${isDarkMode ? "bg-white/[0.06]" : "bg-gray-100"} before:absolute before:left-0 before:inset-y-0 before:w-[3px] ${TONE_ACCENT[meta.tone]}` : ""}`}>
                  <button type="button" onClick={() => onItem(n)} aria-expanded={expanded === n.id} className={`w-full text-left flex gap-3 px-4 py-3 transition-colors ${hover} ${n.read && expanded !== n.id ? "opacity-70" : ""}`}>
                    <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${TONE_CLASS[meta.tone]}`}>
                      <i className={`${meta.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className={`text-sm truncate ${n.read ? "" : "font-semibold"}`}>{notificationTitle(n)}</span>
                        <span className={`text-[11px] shrink-0 tabular-nums ${muted}`}>{ago(n.created_at)}</span>
                      </span>
                      {expanded !== n.id && <span className={`block text-xs mt-0.5 line-clamp-2 ${muted}`}>{n.description}</span>}
                    </span>
                    {!n.read && <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-primary-500" aria-label="Sin leer"></span>}
                    <i className={`ri-arrow-down-s-line shrink-0 mt-0.5 text-base ${muted} transition-transform duration-200 ${expanded === n.id ? "rotate-180" : ""}`} aria-hidden="true"></i>
                  </button>
                  {expanded === n.id && <NotificationDetail n={n} isDarkMode={isDarkMode} onMap={() => onMap(n)} showUnit inset="pl-[48px]" />}
                </li>
              );
            })}
          </ul>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
